// 3rd party imports
import * as React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { observer } from 'mobx-react';

// hooks
import { useStores } from 'hooks';

// config
import { programTermsAndConditionsMapping } from '../../config/program-terms-and-conditions-mapping';

// observability
import telemeter from '../../observability/telemeter';
import { SignupEvents, SIGNUP_TAG } from '../../observability/events/signup';
import { mergeQueryString } from 'bernie-core';

// shared-ui
import { LocalizedText, useLocalization } from '@shared-ui/localization-context';
import { useLogger } from '@shared-ui/logger-context';

// util
import { Validation } from '../../util/validation';
import { modifyLocaleParam } from '../../util/locale-param-transformer';
import { ExceptionIndustryDialog } from './exception-industry-dialog';
import { analyticsEvent } from '../../util/analytics';
import urlFormatter from '../../util/url-formatter';
import { blockedCountries } from '../../config/blocked-countries';
import { isSocialMedia, isValidWebsite } from '../../util/website-validator';

// ui
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkCheckbox, UitkCheckboxGroup } from 'uitk-react-switch';
import { UitkLink } from 'uitk-react-link';
import { UitkLoader } from 'uitk-react-loader';
import { UitkPrimaryButton, UitkButtonSize, UitkTertiaryButton } from 'uitk-react-button';
import { UitkMoreInfoTrigger } from 'uitk-react-more-info-trigger';
import { UitkSpacing, UitkSpacingHr } from 'uitk-react-spacing';
import { LocalizedTextWrapping } from 'components/localized-text-wrapping';
import { hashSha512 } from '../../util/hash-sha512';
import { UitkTabs, UitkTab, UitkTabsBackGroundColorType } from 'uitk-react-tabs';
import { UitkTextInput, UitkEmailInput, UitkPasswordInput, UitkSelect } from 'uitk-react-input';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkEmptyState, UitkEmptyStateActionArea } from 'uitk-react-empty-state';
import { UitkBanner } from 'uitk-react-banner';
import { UitkRadioButton, UitkRadioButtonGroup } from 'uitk-react-radio-button';

//model
import { SignupAHFormProps, SignUpAHFormInput } from './models/sign-up-form';
import { UserV2 } from 'src/models';

// component
import { PzFormInfoDialog } from 'components/signup/pz-form-info-dialog';
import { IndustryDefinitionDialog } from './industry-definition-dialog';

export const SignupAHForm: React.FunctionComponent<SignupAHFormProps> = observer(
  ({ countries, onVerificationEmailError, pzOnly, onPzOnlySubmit }) => {
    const LOGGER = useLogger();
    LOGGER.info(`${SIGNUP_TAG}.components`);

    const history = useHistory();
    const location = useLocation();
    const l10n = useLocalization();

    const { analytics, context, industries, userAuth, user, pzStore } = useStores();
    const { useState, useRef, useEffect } = React;
    const { locale } = context;
    const industriesList = industries.getIndustries();

    const [formError, setFormError] = useState(null);
    const [status, setStatus] = useState(null);
    const [checkboxValue, setCheckboxValue] = useState({ privacy: false, pz: false, program: false });
    const [infoEntry, setInfoEntry] = useState('');
    const [exceptionIndustry, setExceptionIndustry] = useState(null);
    const [websiteErrorMessage, setWebsiteErrorMessage] = useState(null);
    const [pzRadioValue, setPzRadioValue] = useState('pzSignupNo');

    // form progress
    const [stepIndex, setStepIndex] = useState(0);
    const [businessSeen, setBusinessSeen] = useState(false);
    const [trackingSeen, setTrackingSeen] = useState(false);

    const firstNameRef = useRef(null);
    const businessRef = useRef(null);
    const pzRef = useRef(null);
    const emailRef = useRef(null);

    const currentUser = userAuth.userDetails?.body;
    const queryParams = new URLSearchParams(history.location.search);

    const modifiedLocale = modifyLocaleParam(locale);
    const programName = programTermsAndConditionsMapping[modifiedLocale];

    const {
      control,
      formState: { errors, touchedFields },
      handleSubmit,
      watch,
      trigger,
      getValues,
      setValue,
    } = useForm<SignUpAHFormInput>({ mode: 'onBlur' });

    // tab `about you`
    const firstNameField = watch('firstName');
    const lastNameField = watch('lastName');
    const emailField = watch('email');
    const phoneField = watch('phone');
    const countryField = watch('country');

    const firstNameLabel = l10n.formatText('signup.form.label.first_name');
    const lastNameLabel = l10n.formatText('signup.form.label.last_name');
    const emailLabel = l10n.formatText('signup.form.label.email');
    const countryLabel = l10n.formatText('signup.form.label.country');
    const phoneLabel = l10n.formatText('pz.signup.form.label.phone');

    // tab `your business`
    const companyField = watch('company');
    const addressField = watch('address');
    const postcodeField = watch('postcode');
    const websiteField = watch('website');
    const industryField = watch('industry');

    const companyLabel = l10n.formatText('signup.form.label.company');
    const addressLabel = l10n.formatText('pz.signup.form.label.address');
    const postcodeLabel = l10n.formatText('pz.signup.form.label.postcode');
    const websiteLabel = l10n.formatText('signup.form.label.website');
    const industryLabel = l10n.formatText('signup.form.label.industry');

    // tab `tracking info`
    const pzEmailField = watch('pzEmail');
    const pzUsernameField = watch('pzUsername');
    const pzPasswordField = watch('pzPassword');
    const pzCheckboxField = watch('pzCheckbox');
    const privacyCheckboxField = watch('privacyCheckbox');
    const programCheckboxField = watch('programCheckbox');

    const pzUsernameLabel = l10n.formatText('pz.signup.form.label.username');
    const pzPasswordLabel = l10n.formatText('pz.signup.form.label.password');
    const pzTermsAndConditionsLabel = l10n.formatText('pz.signup.form.terms_and_conditions.pz_terms');

    const programTermsAndConditionsLabel = l10n.formatText('pz.signup.form.terms_and_conditions.pz_terms');

    // missing labels tab `about you`
    const firstNameMissingErrorLabel = l10n.formatText('signup.form.message.no_first_name');
    const lastNameMissingErrorLabel = l10n.formatText('signup.form.message.no_last_name');
    const emailMissingErrorLabel = l10n.formatText('signup.form.message.no_email');
    const phoneMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_phone');
    const countryMissingErrorLabel = l10n.formatText('signup.form.message.no_country');

    // missing labels tab `your business`
    const websiteMissingErrorLabel = l10n.formatText('signup.form.message.no_website');
    const companyMissingErrorLabel = l10n.formatText('signup.form.message.no_company');
    const industryMissingErrorLabel = l10n.formatText('signup.form.message.no_industry');
    const addressMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_address');
    const postcodeMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_postcode');

    // missing labels tab `tracking info`
    const hasPzAccountMissingErrorLabel = l10n.formatText('signup.form.message.no_radio_has_pz_account');
    const pzUsernameMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_username');
    const pzPasswordMissingErrorLabel = l10n.formatText('pz.signup.form.message.no_password');
    const privacyMissingErrorLabel = l10n.formatText('signup.form.message.no_privacy_policy');
    const pzTermsAndConditionsMissingErrorLabel = l10n.formatText(
      'pz.signup.form.message.terms_and_conditions.please_accept',
    );
    const programTermsAndConditionsMissingErrorLabel = l10n.formatText(
      'signup.form.message.no_program_terms_and_conditions',
    );

    // invalid labels
    const emailInvalidErrorLabel = l10n.formatText('signup.form.message.invalid_email');
    const websiteInvalidErrorLabel = l10n.formatText('signup.form.message.invalid_url');

    const scrollWindow = (xpos, ypos) => {
      window.scrollTo(xpos, ypos);
    };

    const rfrrIdAboutyouSubmitSelected = 'affhub_signup_aboutyou_submit.selected';
    const linkNameAboutyouSubmitSelected = 'Affhub Signup Aboutyou Submit Selected';
    const rfrrIdAboutbizSubmitSelected = 'affhub_signup_aboutbiz_submit.selected';
    const linkNameAboutbizSubmitSelected = 'Affhub Signup Aboutbiz Submit Selected';
    const rfrrIdSubmitsucceeded = 'affhub_signup_submit.succeeded';
    const linkNameSubmitSuceeded = 'AffHub Signup Submit Succeeded';
    const rfrrIdAccountCreated = 'affhub_account.created';
    const linkNameAccountCreated = 'AffHub Account Created';
    const rfrrIdValidationFailed = 'affhub_signup_validation.failed';
    const linkNameValidationFailed = 'AffHub Signup Validation Failed';
    const referrerIdSignupSelected = 'affhub_signup_submit.selected';
    const linkNameSignupSelected = 'Affhub Sign Up Submit Selected';
    const rfrrIdCreationFailed = 'affhub_account_create.failed';
    const linkNameCreationFailed = 'AffHub Account Creation Failed';
    const rfrrIdBlockedCountryRejection = 'affhub_signup_validation.blockedCountry';
    const linkNameBlockedCountryRejection = 'Affhub Signup Submit Blocked Country';
    const rfrrIdInvalidWebsite = 'affhub_signup_validation.invalidWebsite';
    const linkNameInvalidWebsite = 'Affhub Signup Submit Invalid Website';

    const analyticsTrackEventsMap = new Map([
      [rfrrIdAboutyouSubmitSelected, linkNameAboutyouSubmitSelected],
      [rfrrIdAboutbizSubmitSelected, linkNameAboutbizSubmitSelected],
      [rfrrIdSubmitsucceeded, linkNameSubmitSuceeded],
      [rfrrIdAccountCreated, linkNameAccountCreated],
      [rfrrIdValidationFailed, linkNameValidationFailed],
      [referrerIdSignupSelected, linkNameSignupSelected],
      [rfrrIdCreationFailed, linkNameCreationFailed],
      [rfrrIdBlockedCountryRejection, linkNameBlockedCountryRejection],
      [rfrrIdInvalidWebsite, linkNameInvalidWebsite],
    ]);

    const analyticsTrackEvents = (rfrr: string) => {
      analyticsEvent(analytics, userAuth, pzStore, rfrr, analyticsTrackEventsMap.get(rfrr));
    };

    const onBlurWebsiteField = (onBlurFunction, url) => {
      onBlurFunction();

      if (isSocialMedia(url)) {
        setWebsiteErrorMessage(l10n.formatText('signup.form.message.this_website_doesnt_look_right'));
      } else {
        setWebsiteErrorMessage(null);
      }
    };

    const onSubmit = handleSubmit((fields) => {
      setFormError(null);

      setStatus('LOADING');
      analyticsTrackEvents(rfrrIdSubmitsucceeded);

      // format website url into `https://` if user's input doesn't have `http://` or `https://`
      fields.website = urlFormatter(fields.website.trim());

      if (!blockedCountries.includes(countryField) && isValidWebsite(websiteField)) {
        triggerSignup(fields);
      } else {
        if (!isValidWebsite(websiteField)) {
          analyticsTrackEvents(rfrrIdInvalidWebsite);
        } else if (blockedCountries.includes(countryField)) {
          analyticsTrackEvents(rfrrIdBlockedCountryRejection);
        }
        history.push(`/${modifiedLocale}/about-your-application`);
      }
    });

    const sendVerificationEmail = async (verificationEmail) => {
      try {
        await user.sendVerificationEmail(verificationEmail, modifiedLocale);
        telemeter.increment(SignupEvents.VERIFICATION_EMAIL_SENT);
        LOGGER.info(SignupEvents.VERIFICATION_EMAIL_SENT);
      } catch (err) {
        telemeter.increment(SignupEvents.VERIFICATION_EMAIL_SEND_FAIL);
        LOGGER.error(SignupEvents.VERIFICATION_EMAIL_SEND_FAIL, JSON.stringify(err), {
          username: verificationEmail,
        });
        onVerificationEmailError();
      }
    };

    const handleRadioChange = (event) => {
      setPzRadioValue(event.currentTarget.value);
    };

    useEffect(() => {
      if (!touchedFields?.pzEmail) {
        setValue('pzEmail', watch('email'));
      }
    }, [watch('email'), touchedFields?.pzEmail]);

    // Copied from existing PZ signup form

    const pzPasswordReqs = new Map(
      [
        'at_least_1_uppercase',
        'at_least_1_lowercase',
        'at_least_8_characters',
        'at_least_1_special',
        'at_least_1_number',
      ].map((rule) => [rule, l10n.formatText(`pz.password.rules.${rule}`)]),
    );

    const pzUsernameReqs = new Map(
      [
        'only_alphanumeric',
        'must_be_unique',
        'at_least_2_characters',
        'at_most_32_characters',
        'cannot_contain_accountname',
      ].map((rule) => [rule, l10n.formatText(`pz.username.rules.${rule}`)]),
    );

    const pzRules = {
      username: {
        required: pzUsernameMissingErrorLabel,
        minLength: {
          value: 3,
          message: pzUsernameReqs.get('at_least_2_characters'),
        },
        maxLength: {
          value: 32,
          message: pzUsernameReqs.get('at_most_32_characters'),
        },
        pattern: {
          value: /^\w[0-9a-zA-Z]+$/,
          message: pzUsernameReqs.get('only_alphanumeric'),
        },
      },
      password: {
        required: pzPasswordMissingErrorLabel,
        minLength: {
          value: 8,
          message: pzPasswordReqs.get('at_least_8_characters'),
        },
        validate: {
          upperCase: (value) => value.toLowerCase() !== value || pzPasswordReqs.get('at_least_1_uppercase'),
          lowerCase: (value) => value.toUpperCase() !== value || pzPasswordReqs.get('at_least_1_lowercase'),
          hasNumber: (value) => /\d/.test(value) || pzPasswordReqs.get('at_least_1_number'),
          hasSpecialChar: (value) =>
            /[`!@#$%^&*()_+\-=\]{};':"\\|,.<>?~]/.test(value) || pzPasswordReqs.get('at_least_1_special'),
        },
      },
    };

    const triggerSignup = async (fields: SignUpAHFormInput) => {
      telemeter.increment(SignupEvents.ATTEMPTED);

      const newUser: UserV2 = {
        ...fields,
        pzTermsId: pzStore.termsId,
        isTestUser: !!queryParams.get('testing'),
        pzOnly,
      };

      try {
        const response = await user.createUserV2(newUser);
        setStatus(null);
        setFormError(null);

        if (response.success) {
          if (!pzOnly) {
            analyticsTrackEvents(rfrrIdAccountCreated);
            sendVerificationEmail(fields.email);

            telemeter.increment(SignupEvents.SUCCESS);
            LOGGER.info(SignupEvents.SUCCESS);

            history.push(`/${modifiedLocale}/signup?email=${fields.email.replace('+', '%2B')}`);
          } else {
            onPzOnlySubmit();
          }

          return;
        } else {
          if (response.resBody) {
            if (response.resBody.message === 'USER_ACCOUNT_EXISTS_ERROR') {
              setFormError(l10n.formatText('signup.form.message.this_email_is_in_use'));
              setStepIndex(0);
              emailRef.current.focus();
            } else if (response.resBody.message.includes('user name not unique')) {
              // setFormError(l10n.formatText('signup.form.message.pz_username_not_unique'));
              setFormError('Partnerize username is not unique. Please try again.');
              pzRef.current.focus();
            } else if (response.resBody.message.includes('Email address is in use by another Partnerize account')) {
              // setFormError(l10n.formatText('signup.form.message.pz_email_addr_already_used'));
              setFormError(
                'An account with the same email address already exists in Partnerize. Please try again with a new email address.',
              );
              pzRef.current.focus();
            } else {
              analyticsTrackEvents(rfrrIdCreationFailed);

              telemeter.increment(SignupEvents.FAILED);
              LOGGER.error(SignupEvents.FAILED, JSON.stringify(response.resBody), {
                username: hashSha512(fields.email),
              });
              setFormError(l10n.formatText('common.internal_server_error'));
            }
          } else {
            analyticsTrackEvents(rfrrIdCreationFailed);

            telemeter.increment(SignupEvents.UNKNOWN_FAILURE);
            LOGGER.error(SignupEvents.UNKNOWN_FAILURE, JSON.stringify(response), {
              username: hashSha512(fields.email),
            });

            setFormError(l10n.formatText('common.internal_server_error'));
          }
          scrollWindow(0, 0);
        }
      } catch (err) {
        telemeter.increment(SignupEvents.FAILED_ON_CREATING_USER);
        LOGGER.error(SignupEvents.FAILED_ON_CREATING_USER, JSON.stringify(err), {
          username: hashSha512(fields.email),
        });
      }
    };

    const analyticsSignupSelected = {
      id: referrerIdSignupSelected,
      callback: () => {
        analyticsTrackEvents(referrerIdSignupSelected);
      },
    };

    const handleClick = async (e) => {
      // for last step, it will do `submit` action by default so we don't have to specify it here
      if (stepIndex < 2) {
        e.preventDefault();

        if (stepIndex === 0) {
          analyticsTrackEvents(rfrrIdAboutyouSubmitSelected);
          const result = await trigger(tab0);
          if (result) {
            setStepIndex(stepIndex + 1);
          }
        } else if (stepIndex === 1) {
          analyticsTrackEvents(rfrrIdAboutbizSubmitSelected);
          const result = await trigger(tab1);
          let dialogQuery;

          switch (getValues('industry')) {
            case 'travel_agents':
              dialogQuery = mergeQueryString(location.search, { pwaDialog: 'exception-industry' });
              setExceptionIndustry('travel_agents');
              history.push({ ...location, search: dialogQuery });
              setStatus(null);
              break;
            case 'sub-affiliate_networks':
              dialogQuery = mergeQueryString(location.search, { pwaDialog: 'exception-industry' });
              history.push({ ...location, search: dialogQuery });
              setExceptionIndustry('sub-affiliate_networks');
              setStatus(null);
              break;
            default:
              if (result) {
                setStepIndex(stepIndex + 1);
              }
          }
        }
      }
    };

    useEffect(() => {
      // fetch Pz terms based on country field input
      if (getValues('country')) {
        pzStore.fetchTerms(getValues('country'), locale);
      }
    }, [watch('country')]);

    useEffect(() => {
      switch (stepIndex) {
        case 1:
          if (businessRef.current) {
            setBusinessSeen(true);
            setTimeout(() => {
              businessRef.current.focus();
            });
          }
          break;
        case 2:
          setTrackingSeen(true);
          break;
      }
    }, [stepIndex, businessRef, pzRef]);

    useEffect(() => {
      firstNameRef?.current.focus();

      if (Object.keys(errors).length) {
        analyticsTrackEvents(rfrrIdValidationFailed);
      }
    }, [firstNameRef, errors]);

    if (pzStore?.termsState === 'error') {
      return (
        <UitkSpacing margin={{ blockstart: 'three' }}>
          <div className="SignupPz__error">
            <UitkSpacingHr />
            <UitkEmptyState>
              <UitkIcon name="sentiment_2" size={UitkIconSize.LARGE} />
              <UitkHeading size={5} tag="h4">
                We&apos;re having trouble loading the form
              </UitkHeading>
              <UitkText size={300}>Click below to reload the page or try again later.</UitkText>
              <UitkEmptyStateActionArea>
                <UitkTertiaryButton name="textonly" type="button" onClick={() => window.location.reload()}>
                  Reload
                </UitkTertiaryButton>
              </UitkEmptyStateActionArea>
            </UitkEmptyState>
          </div>
        </UitkSpacing>
      );
    }

    // Tab group
    // declare the type so we can call `trigger` later
    const tab0: ['firstName', 'lastName', 'email', 'phone', 'country'] = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'country',
    ];
    const tab1: ['company', 'address', 'website', 'industry', 'postcode'] = [
      'company',
      'address',
      'website',
      'industry',
      'postcode',
    ];
    const tab2a = ['privacyCheckbox'];
    const tab2b = ['pzEmail', 'pzUsername', 'pzPassword', 'pzCheckbox'];

    const tabErrors = (tabIndex) => {
      const tabError = (list) => {
        const errorObject = {};
        list.forEach((tabName) => {
          if (errors[tabName]) {
            errorObject[tabName] = errors[tabName];
          }
        });

        return errorObject;
      };
      switch (tabIndex) {
        case 0:
          return tabError(tab0);
        case 1:
          return tabError(tab1);
        case 2:
          if (!pzOnly) {
            return tabError([...tab0, ...tab1, ...tab2a, ...tab2b]);
          } else {
            return tabError([...tab0, ...tab1, ...tab2b]);
          }
      }
    };

    const handleDisabled = () => {
      const hasErrors = Object.keys(tabErrors(stepIndex)).length > 0;
      const isLoading = status === 'LOADING';
      const isWebsiteInvalid = !!websiteErrorMessage; // return boolean
      let missingInputs: boolean;
      if (stepIndex === 0) {
        missingInputs = !firstNameField || !lastNameField || !emailField || !phoneField || !countryField;
      } else if (stepIndex === 1) {
        missingInputs = !companyField || !addressField || !websiteField || !industryField || !postcodeField;
      } else if (stepIndex === 2) {
        if (!pzOnly) {
          missingInputs =
            !pzEmailField ||
            !pzUsernameField ||
            !pzPasswordField ||
            !privacyCheckboxField ||
            !pzCheckboxField ||
            !programCheckboxField;
        } else {
          missingInputs =
            !pzEmailField || !pzUsernameField || !pzPasswordField || !pzCheckboxField || !programCheckboxField;
        }
      }

      return hasErrors || isLoading || missingInputs || isWebsiteInvalid;
    };

    const pzUsernamePasswordBlock = (
      <>
        <UitkSpacing margin={{ blockend: 'two' }}>
          <div>
            <UitkSpacing margin={{ block: 'two' }}>
              <UitkMoreInfoTrigger onClick={() => setInfoEntry('username')}>
                <Link
                  to={{
                    pathname: location.pathname,
                    search: mergeQueryString(location.search, {
                      pwaDialog: 'pzFormInfo',
                    }),
                  }}
                  role="button"
                >
                  <UitkText size={300}>{pzUsernameLabel}</UitkText>
                  <UitkIcon name="info_outline" />
                </Link>
              </UitkMoreInfoTrigger>
            </UitkSpacing>
            <Controller
              control={control}
              name="pzUsername"
              rules={pzRules.username}
              render={({ field: { onChange, onBlur, value, name } }) => (
                <UitkTextInput
                  domRef={pzRef}
                  id="signUpFormUsernameInput"
                  name={name}
                  label={pzUsernameLabel}
                  value={value}
                  onChange={({ target: { value: newValue } }) => {
                    onChange(newValue);
                  }}
                  onBlur={onBlur}
                  data-cy="sign-up-username-field"
                  invalid={errors.pzUsername?.message}
                />
              )}
            />
          </div>
        </UitkSpacing>
        <UitkSpacing margin={{ blockend: 'three' }}>
          <div>
            <UitkSpacing margin={{ block: 'two' }}>
              <UitkMoreInfoTrigger onClick={() => setInfoEntry('password')}>
                <Link
                  to={{
                    pathname: location.pathname,
                    search: mergeQueryString(location.search, {
                      pwaDialog: 'pzFormInfo',
                    }),
                  }}
                  role="button"
                >
                  <UitkText size={300}>{pzPasswordLabel}</UitkText>
                  <UitkIcon name="info_outline" />
                </Link>
              </UitkMoreInfoTrigger>
            </UitkSpacing>
            <Controller
              control={control}
              name="pzPassword"
              rules={pzRules.password}
              render={({ field: { onChange, onBlur, value, name } }) => (
                <UitkPasswordInput
                  id="passwordInput"
                  label={pzPasswordLabel}
                  name={name}
                  value={value}
                  onChange={({ target: { value: passwordValue } }) => {
                    onChange(passwordValue);
                  }}
                  onBlur={onBlur}
                  invalid={errors.pzPassword?.message}
                  data-cy="sign-up-password-field"
                />
              )}
            />
          </div>
        </UitkSpacing>
      </>
    );

    const onboardingPz = () => {
      return (
        <>
          <UitkSpacing margin={{ blockend: 'two' }}>
            <UitkHeading>
              <LocalizedText message="pz.signup.form.partnerize_details" />
            </UitkHeading>
          </UitkSpacing>

          <UitkSpacing margin={{ block: 'three' }}>
            <UitkText size={300}>
              <LocalizedText message="pz.signup.text.partnerize_is_the_third_party_affiliate_network" />{' '}
              <UitkLink inline={true}>
                <a
                  href="https://egaffiliates.zendesk.com/hc/en-us/articles/1500003709001"
                  target="_blank"
                  rel="noreferrer"
                >
                  <LocalizedText message="pz.signup.link.learn_more" />
                </a>
              </UitkLink>
            </UitkText>
          </UitkSpacing>

          <UitkSpacing margin={{ block: 'three' }}>
            <UitkRadioButtonGroup group="pzaccount" onChange={handleRadioChange} selected={pzRadioValue}>
              <UitkLayoutFlex space="two">
                <UitkLayoutFlexItem>
                  <UitkRadioButton id="No" value="pzSignupNo">
                    <LocalizedText message="pz.signup.radio.create_new_account" />
                  </UitkRadioButton>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                  <UitkRadioButton id="Yes" value="pzSignupYes">
                    <LocalizedText message="pz.signup.radio.use_existing_account" />
                  </UitkRadioButton>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkRadioButtonGroup>
          </UitkSpacing>

          {pzRadioValue === 'pzSignupYes' && (
            <UitkSpacing margin={{ blockend: 'three', blockstart: 'three' }}>
              <div>
                <UitkBanner
                  icon="warning"
                  heading={l10n.formatText('pz.signup.message.existing_accounts_unsupported')}
                  headingLevel="h2"
                  message={l10n.formatText('pz.signup.message.you_can_continue_to_earn_commissions_from_expedia')}
                />
              </div>
            </UitkSpacing>
          )}

          {pzRadioValue === 'pzSignupNo' && (
            <UitkSpacing margin={{ blockend: 'two', blockstart: 'three' }}>
              <div>
                <UitkSpacing margin={{ blockend: 'two' }}>
                  <UitkText size={300} theme="default" weight="regular">
                    <LocalizedText message={'pz.signup.form.label.partnerize_email'} />
                  </UitkText>
                </UitkSpacing>
                <Controller
                  defaultValue={watch('email')}
                  control={control}
                  name="pzEmail"
                  rules={{
                    required: l10n.formatText('signup.form.message.no_email'),
                    pattern: {
                      value: Validation.EMAIL_REGEX,
                      message: l10n.formatText('signup.form.message.invalid_email'),
                    },
                  }}
                  render={({ field: { onChange, onBlur, value, name } }) => (
                    <UitkEmailInput
                      id="signUpPzFormEmailInput"
                      name={name}
                      label={l10n.formatText('pz.signup.form.label.partnerize_email')}
                      value={value}
                      onChange={({ target: { value: newValue } }) => {
                        onChange(newValue);
                      }}
                      onBlur={onBlur}
                      invalid={errors.pzEmail?.message}
                      data-cy="sign-up-email-field"
                      domRef={emailRef}
                    />
                  )}
                />
              </div>
            </UitkSpacing>
          )}
          {pzRadioValue === 'pzSignupNo' && pzUsernamePasswordBlock}
        </>
      );
    };

    const trackingInfoTab = () => {
      let tabContent;
      if (!pzOnly) {
        tabContent = (
          <div className="SignupAHForm__trackingInfo">
            {onboardingPz()}

            <UitkCheckboxGroup group="checkboxGroup">
              {!pzOnly && pzRadioValue === 'pzSignupNo' && (
                <Controller
                  control={control}
                  name="privacyCheckbox"
                  defaultValue={checkboxValue.privacy}
                  rules={{
                    required: privacyMissingErrorLabel,
                  }}
                  render={({ field: { onChange, value, name } }) => (
                    <UitkCheckbox
                      id="privacyCheckbox"
                      name={name}
                      checked={value}
                      onChange={({ target: { checked } }) => {
                        onChange(checked);
                        setCheckboxValue({ ...checkboxValue, privacy: checked });
                      }}
                      errorMessage={errors.privacyCheckbox?.message}
                      data-cy="sign-up-privacy-field"
                    >
                      <LocalizedTextWrapping
                        message="signup.form.label.text.i_have_read_the_privacy_policy"
                        fieldName="privacy_policy"
                      >
                        <UitkLink inline={true}>
                          <Link to={`/${modifiedLocale}/doc/privacy-policy`} target="_blank">
                            <LocalizedText message="signup.form.label.linkname.privacy_policy" />
                          </Link>
                        </UitkLink>
                      </LocalizedTextWrapping>
                    </UitkCheckbox>
                  )}
                />
              )}

              <Controller
                control={control}
                name="pzCheckbox"
                defaultValue={checkboxValue.pz}
                rules={{
                  required: pzTermsAndConditionsMissingErrorLabel,
                }}
                render={({ field: { onChange, value, name } }) => (
                  <div className={`PzCheckbox ${pzStore?.termsState === 'pending' ? 'PzCheckbox--pending' : ''}`}>
                    <UitkLoader isSmall={true} className="PzCheckbox__spinner" />
                    {pzRadioValue === 'pzSignupNo' && (
                      <UitkCheckbox
                        id="pzCheckbox"
                        name={name}
                        label={pzTermsAndConditionsLabel}
                        checked={checkboxValue.pz}
                        disabled={!(pzStore?.terms?.length > 0)}
                        onChange={({ target: { checked } }) => {
                          onChange(checked);
                          setCheckboxValue({ ...checkboxValue, pz: checked });
                        }}
                        errorMessage={errors.pzCheckbox?.message}
                        data-cy="sign-up-pz-field"
                      >
                        <LocalizedTextWrapping
                          message="pz.signup.form.terms_and_conditions.i_have_read_and_accept_the_terms"
                          fieldName="termsAndConditions"
                        >
                          <UitkLink inline={true}>
                            <Link
                              to={{
                                pathname: location.pathname,
                                search: mergeQueryString(location.search, {
                                  pwaDialog: 'pzTerms',
                                }),
                              }}
                              role="button"
                            >
                              <LocalizedText message="pz.signup.form.terms_and_conditions.pz_terms" />
                            </Link>
                          </UitkLink>
                        </LocalizedTextWrapping>
                      </UitkCheckbox>
                    )}
                  </div>
                )}
              />
              <Controller
                control={control}
                name="programCheckbox"
                defaultValue={checkboxValue.program}
                rules={{
                  required: programTermsAndConditionsMissingErrorLabel,
                }}
                render={({ field: { onChange, value, name } }) =>
                  pzRadioValue === 'pzSignupNo' && (
                    <UitkCheckbox
                      id="programCheckbox"
                      name={name}
                      label={programTermsAndConditionsLabel}
                      checked={checkboxValue.program}
                      onChange={({ target: { checked } }) => {
                        onChange(checked);
                        setCheckboxValue({ ...checkboxValue, program: checked });
                      }}
                      errorMessage={errors.programCheckbox?.message}
                      data-cy="sign-up-program-field"
                    >
                      <LocalizedTextWrapping
                        message="signup.form.label.text.i_have_read_the_program_terms"
                        fieldName="affiliate_program_terms_and_conditions"
                      >
                        <UitkLink inline={true}>
                          <Link
                            to={{
                              pathname: `/${modifiedLocale}/entity-terms-and-conditions`,
                              search: `?programName=${programName}`,
                            }}
                            target={'_blank'}
                          >
                            {l10n.formatText('signup.form.label.linkname.program_terms')}
                          </Link>
                        </UitkLink>
                      </LocalizedTextWrapping>
                    </UitkCheckbox>
                  )
                }
              />
            </UitkCheckboxGroup>
          </div>
        );
      } else {
        tabContent = (
          <>
            <UitkSpacing margin={{ blockend: 'two' }}>
              <UitkHeading>
                <LocalizedText message="pz.signup.form.create_your_account" />
              </UitkHeading>
            </UitkSpacing>

            <UitkSpacing margin={{ block: 'three' }}>
              <UitkText size={300}>
                <LocalizedText message="pz.signup.text.partnerize_is_the_third_party_affiliate_network" />{' '}
                <UitkLink inline={true}>
                  <a
                    href="https://egaffiliates.zendesk.com/hc/en-us/articles/1500003709001"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LocalizedText message="pz.signup.link.learn_more" />
                  </a>
                </UitkLink>
              </UitkText>
            </UitkSpacing>

            {pzUsernamePasswordBlock}

            <UitkCheckboxGroup group="checkboxGroup">
              <Controller
                control={control}
                name="pzCheckbox"
                defaultValue={checkboxValue.pz}
                rules={{
                  required: pzTermsAndConditionsMissingErrorLabel,
                }}
                render={({ field: { onChange, value, name } }) => (
                  <div className={`PzCheckbox ${pzStore?.termsState === 'pending' ? 'PzCheckbox--pending' : ''}`}>
                    <UitkLoader isSmall={true} className="PzCheckbox__spinner" />
                    {pzRadioValue === 'pzSignupNo' && (
                      <UitkCheckbox
                        id="pzCheckbox"
                        name={name}
                        label={pzTermsAndConditionsLabel}
                        checked={checkboxValue.pz}
                        disabled={!(pzStore?.terms?.length > 0)}
                        onChange={({ target: { checked } }) => {
                          onChange(checked);
                          setCheckboxValue({ ...checkboxValue, pz: checked });
                        }}
                        errorMessage={errors.pzCheckbox?.message}
                        data-cy="sign-up-pz-field"
                      >
                        <LocalizedTextWrapping
                          message="pz.signup.form.terms_and_conditions.i_have_read_and_accept_the_terms"
                          fieldName="termsAndConditions"
                        >
                          <UitkLink inline={true}>
                            <Link
                              to={{
                                pathname: location.pathname,
                                search: mergeQueryString(location.search, {
                                  pwaDialog: 'pzTerms',
                                }),
                              }}
                              role="button"
                            >
                              <LocalizedText message="pz.signup.form.terms_and_conditions.pz_terms" />
                            </Link>
                          </UitkLink>
                        </LocalizedTextWrapping>
                      </UitkCheckbox>
                    )}
                  </div>
                )}
              />
            </UitkCheckboxGroup>

            <Controller
              control={control}
              name="programCheckbox"
              defaultValue={checkboxValue.program}
              rules={{
                required: programTermsAndConditionsMissingErrorLabel,
              }}
              render={({ field: { onChange, value, name } }) =>
                pzRadioValue === 'pzSignupNo' && (
                  <UitkCheckbox
                    id="programCheckbox"
                    name={name}
                    label={programTermsAndConditionsLabel}
                    checked={checkboxValue.program}
                    onChange={({ target: { checked } }) => {
                      onChange(checked);
                      setCheckboxValue({ ...checkboxValue, program: checked });
                    }}
                    errorMessage={errors.programCheckbox?.message}
                    data-cy="sign-up-program-field"
                  >
                    <LocalizedTextWrapping
                      message="signup.form.label.text.i_have_read_the_program_terms"
                      fieldName="affiliate_program_terms_and_conditions"
                    >
                      <UitkLink inline={true}>
                        <Link
                          to={{
                            pathname: `/${modifiedLocale}/entity-terms-and-conditions`,
                            search: `?programName=${programName}`,
                          }}
                          target={'_blank'}
                        >
                          {l10n.formatText('signup.form.label.linkname.program_terms')}
                        </Link>
                      </UitkLink>
                    </LocalizedTextWrapping>
                  </UitkCheckbox>
                )
              }
            />
          </>
        );
      }

      return tabContent;
    };

    return (
      <>
        <IndustryDefinitionDialog />
        <ExceptionIndustryDialog l10n={l10n} industry={exceptionIndustry} />
        <PzFormInfoDialog
          dialogId="pzFormInfo"
          infoEntry={infoEntry}
          l10n={l10n}
          rules={{
            username: Array.from(pzUsernameReqs.values()),
            password: Array.from(pzPasswordReqs.values()),
          }}
        />
        <form
          className={`container container--small ${pzOnly ? 'pz-only' : ''}`}
          name={'signUpForm'}
          onSubmit={onSubmit}
          method="POST"
        >
          {formError && (
            <UitkSpacing margin={{ blockend: 'six' }}>
              <div>
                <UitkErrorSummary heading={formError} />
              </div>
            </UitkSpacing>
          )}

          {!!queryParams.get('testing') && <UitkBanner message="Hi, you are creating a test account!" />}

          <UitkSpacing margin={{ block: 'four' }}>
            <UitkTabs
              backgroundColor={UitkTabsBackGroundColorType.SECONDARY}
              selectedTab={stepIndex}
              onTabSelect={(i) => setStepIndex(i)}
            >
              <UitkTab name={l10n.formatText('signup.form.label.about_you')} id="aboutTab" targetURI="?step=about">
                <>
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <UitkLayoutFlex>
                      <UitkLayoutFlexItem grow={1} className={'SignupAHForm__gap'}>
                        <div>
                          <Controller
                            control={control}
                            name="firstName"
                            defaultValue={currentUser?.firstName}
                            rules={{
                              required: firstNameMissingErrorLabel,
                            }}
                            render={({ field: { onChange, onBlur, value, name } }) => (
                              <UitkTextInput
                                domRef={firstNameRef}
                                id="signUpFormFirstNameInput"
                                name={name}
                                label={firstNameLabel}
                                value={value}
                                onChange={({ target: { value: newValue } }) => {
                                  onChange(newValue);
                                }}
                                onBlur={onBlur}
                                invalid={errors.firstName?.message}
                                data-cy="sign-up-first-name-field"
                              />
                            )}
                          />
                        </div>
                      </UitkLayoutFlexItem>

                      <UitkLayoutFlexItem grow={1}>
                        <div>
                          <Controller
                            control={control}
                            name="lastName"
                            defaultValue={currentUser?.lastName}
                            rules={{
                              required: lastNameMissingErrorLabel,
                            }}
                            render={({ field: { onChange, onBlur, value, name } }) => (
                              <UitkTextInput
                                id="signUpFormLastNameInput"
                                name={name}
                                label={lastNameLabel}
                                value={value}
                                onChange={({ target: { value: newValue } }) => {
                                  onChange(newValue);
                                }}
                                onBlur={onBlur}
                                invalid={errors.lastName?.message}
                                data-cy="sign-up-last-name-field"
                              />
                            )}
                          />
                        </div>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <div>
                      <Controller
                        control={control}
                        name="email"
                        defaultValue={currentUser?.id}
                        rules={{
                          required: emailMissingErrorLabel,
                          pattern: {
                            value: Validation.EMAIL_REGEX,
                            message: emailInvalidErrorLabel,
                          },
                        }}
                        render={({ field: { onChange, onBlur, value, name } }) => (
                          <UitkEmailInput
                            id="signUpFormEmailInput"
                            name={name}
                            label={emailLabel}
                            value={value}
                            onChange={({ target: { value: newValue } }) => {
                              onChange(newValue);
                            }}
                            onBlur={onBlur}
                            invalid={errors.email?.message}
                            data-cy="sign-up-email-field"
                            domRef={emailRef}
                          />
                        )}
                      />
                    </div>
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <div>
                      <Controller
                        control={control}
                        name="phone"
                        rules={{
                          required: phoneMissingErrorLabel,
                        }}
                        render={({ field: { onChange, onBlur, value, name } }) => (
                          <UitkTextInput
                            id="signUpFormPhoneInput"
                            value={value?.toString()}
                            name={name}
                            label={phoneLabel}
                            onChange={({ target: { value: newValue } }) => {
                              onChange(newValue);
                            }}
                            onBlur={onBlur}
                            invalid={errors.phone?.message}
                            data-cy="sign-up-phone-field"
                          />
                        )}
                      />
                    </div>
                  </UitkSpacing>
                  <Controller
                    control={control}
                    name="country"
                    defaultValue={currentUser?.country}
                    rules={{
                      required: countryMissingErrorLabel,
                    }}
                    render={({ field: { onChange, onBlur, value, name } }) => (
                      <UitkSelect
                        id="signUpFormCountryInput"
                        name={name}
                        label={countryLabel}
                        value={value}
                        onChange={({ target: { value: newValue } }) => {
                          onChange(newValue);
                        }}
                        onBlur={onBlur}
                        emptyState={true}
                        invalid={errors.country?.message}
                        data-cy="sign-up-country-field"
                      >
                        {countries &&
                          [...countries].map((item, i) => {
                            return (
                              <option key={i} value={item[0]}>
                                {item[1]}
                              </option>
                            );
                          })}
                      </UitkSelect>
                    )}
                  />
                </>
              </UitkTab>
              <UitkTab
                name={l10n.formatText('signup.form.label.your_business')}
                id="businessTab"
                targetURI="?step=business"
                disabled={!businessSeen}
              >
                <>
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <div>
                      <Controller
                        control={control}
                        name="company"
                        defaultValue={currentUser?.company}
                        rules={{
                          required: companyMissingErrorLabel,
                        }}
                        render={({ field: { onChange, onBlur, value, name } }) => (
                          <UitkTextInput
                            id="signUpFormCompanyInput"
                            name={name}
                            label={companyLabel}
                            value={value}
                            domRef={businessRef}
                            onChange={({ target: { value: newValue } }) => {
                              onChange(newValue);
                            }}
                            onBlur={onBlur}
                            invalid={errors.company?.message}
                            data-cy="sign-up-company-field"
                          />
                        )}
                      />
                    </div>
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <div>
                      <UitkLayoutFlex wrap="wrap" space="two">
                        <UitkLayoutFlexItem grow={4}>
                          <div>
                            <Controller
                              control={control}
                              name="address"
                              rules={{
                                required: addressMissingErrorLabel,
                              }}
                              render={({ field: { onChange, onBlur, value, name } }) => (
                                <UitkTextInput
                                  id="signUpFormAddressInput"
                                  name={name}
                                  label={addressLabel}
                                  value={value}
                                  onChange={({ target: { value: newValue } }) => {
                                    onChange(newValue);
                                  }}
                                  onBlur={onBlur}
                                  invalid={errors.address?.message}
                                  data-cy="sign-up-address-1-field"
                                />
                              )}
                            />
                          </div>
                        </UitkLayoutFlexItem>
                        <UitkLayoutFlexItem grow={1} minWidth="twenty_four">
                          <div>
                            <Controller
                              control={control}
                              name="postcode"
                              rules={{
                                required: postcodeMissingErrorLabel,
                              }}
                              render={({ field: { onChange, onBlur, value, name } }) => (
                                <UitkTextInput
                                  id="signUpFormPostcodeInput"
                                  name={name}
                                  label={postcodeLabel}
                                  value={value}
                                  onChange={({ target: { value: newValue } }) => {
                                    onChange(newValue);
                                  }}
                                  onBlur={onBlur}
                                  invalid={errors.postcode?.message}
                                  data-cy="sign-up-postcode-field"
                                />
                              )}
                            />
                          </div>
                        </UitkLayoutFlexItem>
                      </UitkLayoutFlex>
                    </div>
                  </UitkSpacing>
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <div>
                      <Controller
                        control={control}
                        name="website"
                        defaultValue={currentUser?.website}
                        rules={{
                          required: websiteMissingErrorLabel,
                          pattern: {
                            value: Validation.VALID_URL,
                            message: websiteInvalidErrorLabel,
                          },
                        }}
                        render={({ field: { onChange, onBlur, value, name } }) => (
                          <UitkTextInput
                            id="signUpFormWebsiteInput"
                            name={name}
                            label={websiteLabel}
                            value={value}
                            onChange={({ target: { value: newValue } }) => {
                              onChange(newValue);
                            }}
                            onBlur={() => onBlurWebsiteField(onBlur, value)}
                            invalid={errors.website?.message}
                            data-cy="sign-up-website-field"
                          />
                        )}
                      />
                    </div>
                  </UitkSpacing>
                  {websiteErrorMessage && (
                    <UitkSpacing margin={{ blockend: 'two' }}>
                      <div className="SignupAHForm__websiteError">
                        <UitkIcon name="info" /> <span>{websiteErrorMessage}</span>
                      </div>
                    </UitkSpacing>
                  )}
                  <UitkSpacing margin={{ blockend: 'two' }}>
                    <div>
                      <Controller
                        control={control}
                        name="industry"
                        defaultValue={currentUser?.industry}
                        rules={{
                          required: industryMissingErrorLabel,
                        }}
                        render={({ field: { onChange, onBlur, value, name } }) => (
                          <UitkSelect
                            id="signUpFormIndustryInput"
                            name={name}
                            label={industryLabel}
                            value={value}
                            onChange={({ target: { value: newValue } }) => {
                              onChange(newValue);
                            }}
                            onBlur={onBlur}
                            invalid={errors.industry?.message}
                            emptyState={true}
                            data-cy="sign-up-industry-field"
                          >
                            {industriesList &&
                              industriesList.map((industry, i) => (
                                <option key={i} value={industry}>
                                  {l10n.formatText(`affiliate.industry.${industry}`)}
                                </option>
                              ))}
                          </UitkSelect>
                        )}
                      />
                    </div>
                  </UitkSpacing>
                </>

                <div className="SignupAHForm__industry">
                  <LocalizedText message="industry.dialog.intro.not_sure_which_industry" />{' '}
                  <UitkLink inline={true}>
                    <Link
                      to={{
                        pathname: location.pathname,
                        search: mergeQueryString(location.search, {
                          pwaDialog: 'industry-definition',
                        }),
                      }}
                      role="button"
                    >
                      <LocalizedText message="industry.dialog.link.read_more" />
                    </Link>
                  </UitkLink>
                </div>
              </UitkTab>
              <UitkTab
                name={l10n.formatText('signup.form.label.tracking_info')}
                id="trackingTab"
                targetURI="?step=tracking"
                disabled={!trackingSeen}
              >
                {trackingInfoTab()}
              </UitkTab>
            </UitkTabs>
          </UitkSpacing>

          <UitkSpacing margin={{ blockstart: 'six', blockend: 'three' }}>
            <UitkPrimaryButton
              analytics={analyticsSignupSelected}
              data-cy="sign-up-submit"
              id="signUpFormSubmitButton"
              type="submit"
              className={'spacing-top-bottom-24'}
              disabled={handleDisabled()}
              size={UitkButtonSize.LARGE}
              onClick={handleClick}
              isFullWidth={true}
            >
              {status === 'LOADING' ? (
                <UitkLoader isSmall={true} isDarkBackground={true} />
              ) : stepIndex === 2 ? (
                l10n.formatText('signup.form.label.submit')
              ) : (
                l10n.formatText('common.next')
              )}
            </UitkPrimaryButton>
          </UitkSpacing>

          {!pzOnly && (
            <UitkText>
              <LocalizedText message="signup.form.label.have_an_account" />{' '}
              <UitkLink inline={true} size="large">
                <Link to={`/${modifiedLocale}/signin`}>
                  <LocalizedText message="signup.form.label.login" />
                </Link>
              </UitkLink>
            </UitkText>
          )}
        </form>
      </>
    );
  },
);
